import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const VERSION_URL = '/version.json';
const CHECK_INTERVAL_MS = 60_000;
const AUTO_REFRESH_AFTER_BACKGROUND_MS = 15_000;
const LEGACY_CACHE_CLEANUP_KEY = 'librarydesk.legacyCacheCleanup.v1';
const LEGACY_CACHE_RELOAD_KEY = 'librarydesk.legacyCacheReloaded.v1';

const fetchCurrentVersion = async () => {
  if (typeof fetch !== 'function') return null;
  const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!response.ok) throw new Error(`Version check failed (${response.status})`);
  const payload = await response.json();
  return typeof payload?.version === 'string' && payload.version ? payload.version : null;
};

const clearLegacyBrowserCaches = async () => {
  if (typeof window === 'undefined') return;
  if (window.localStorage.getItem(LEGACY_CACHE_CLEANUP_KEY) === 'done') return;

  const hadController = Boolean(navigator.serviceWorker?.controller);
  const tasks = [];

  if (navigator.serviceWorker?.getRegistrations) {
    tasks.push(
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
    );
  }

  if (window.caches?.keys) {
    tasks.push(
      window.caches.keys()
        .then((keys) => Promise.all(keys.map((key) => window.caches.delete(key))))
    );
  }

  try {
    await Promise.all(tasks);
    window.localStorage.setItem(LEGACY_CACHE_CLEANUP_KEY, 'done');

    // Unregistering a worker stops future navigations, but the current page can
    // remain controlled until one reload. Perform that reload only once.
    if (hadController && window.sessionStorage.getItem(LEGACY_CACHE_RELOAD_KEY) !== 'done') {
      window.sessionStorage.setItem(LEGACY_CACHE_RELOAD_KEY, 'done');
      window.location.reload();
    }
  } catch (error) {
    // Cache cleanup must never block CRM startup. A future load can try again.
    console.warn('Legacy browser-cache cleanup could not finish:', error);
  }
};

const VersionUpdateManager = () => {
  const loadedVersion = useRef(null);
  const hiddenAt = useRef(null);
  const reloadStarted = useRef(false);
  const [updateVersion, setUpdateVersion] = useState(null);

  const reloadLatest = useCallback(() => {
    if (reloadStarted.current) return;
    reloadStarted.current = true;
    window.location.reload();
  }, []);

  const checkVersion = useCallback(async ({ autoRefresh = false } = {}) => {
    try {
      const latestVersion = await fetchCurrentVersion();
      if (!latestVersion) return;

      if (!loadedVersion.current) {
        loadedVersion.current = latestVersion;
        return;
      }

      if (latestVersion !== loadedVersion.current) {
        setUpdateVersion(latestVersion);
        if (autoRefresh) reloadLatest();
      }
    } catch (error) {
      // Offline use and short hosting/network interruptions are expected. The
      // next focus, visibility event or interval retries silently.
      console.debug('CRM version check skipped:', error?.message || error);
    }
  }, [reloadLatest]);

  useEffect(() => {
    clearLegacyBrowserCaches();
    checkVersion();

    const intervalId = window.setInterval(() => checkVersion(), CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
        return;
      }

      const backgroundDuration = hiddenAt.current ? Date.now() - hiddenAt.current : 0;
      hiddenAt.current = null;
      checkVersion({ autoRefresh: backgroundDuration >= AUTO_REFRESH_AFTER_BACKGROUND_MS });
    };
    const onFocus = () => checkVersion();
    const onPageShow = (event) => checkVersion({ autoRefresh: Boolean(event.persisted) });

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [checkVersion]);

  if (!updateVersion) return null;

  return (
    <div
      className="fixed inset-x-3 top-3 z-[10000] mx-auto flex max-w-xl items-center gap-3 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-white shadow-2xl sm:inset-x-auto sm:right-5 sm:top-5 sm:mx-0"
      role="status"
      aria-live="polite"
      data-testid="crm-update-banner"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-500/15 text-rose-300">
        <RefreshCw size={19} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">A new LibraryDesk update is ready</p>
        <p className="mt-0.5 text-xs text-slate-300">Refresh once to use the latest CRM.</p>
      </div>
      <button
        type="button"
        onClick={reloadLatest}
        className="min-h-11 shrink-0 rounded-xl bg-white px-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
      >
        Refresh
      </button>
    </div>
  );
};

export default VersionUpdateManager;
